import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { registerComponent } from 'react-register-dom'
import NoResults from '@components/NoResults/NoResults'
import ErrorWrapper from '@components/UI/ErrorWrapper'
import { useAutoGET } from '@hooks/fetch'

import { ProductUnit } from './ProductUnit/ProductUnit'
import { ProductUnitCategoryTag } from './ProductUnitCategoryTag/ProductUnitCategoryTag'
import { ProductUnitMachines } from './ProductUnitMachines/ProductUnitMachines'

interface ProductUnitData {
  Title: string
  Category: string
  Image: string
  link: string
  [key: string]: any
}

interface TranslationItem {
  'Product Unit': string
  'Attributes to be translated': string
  [key: string]: string
}

interface Config {
  endpointUrl: string
  translationsEndpointUrl: string
  selectMachineLabel: string
  applyButtonLabel: string
  resetButtonLabel: string
  closeButtonAriaLabel: string
  backButtonLabel: string
  viewPageButtonLabel: string
  noResultsLabel: string
}

interface ProductUnitsProperties {
  jsonData: string
}

const FIXED_FIELDS = new Set(['Title', 'Category', 'Image', 'link'])
const FEATURE_PREFIX = 'Feature'
const FALLBACK_LANGUAGE = '[EN]'

const extractLanguageFromUrl = (pathname: string): string => {
  const urlMatch = pathname.match(/\/(?:content\/franke\/)?([a-z]{2})\/([a-z]{2})(?:\/|$)/i)

  if (urlMatch) {
    const [, country, language] = urlMatch
    return `[${country.toUpperCase()}-${language.toUpperCase()}]`
  }

  return FALLBACK_LANGUAGE
}

const createTranslationMap = (
  translationsData: TranslationItem[],
  currentLanguage: string,
): Map<string, Map<string, string>> => {
  const map = new Map<string, Map<string, string>>()
  const [, language] = currentLanguage.replace(/[[\]]/g, '').split('-')

  for (const item of translationsData) {
    const productUnit = item['Product Unit']
    const attribute = item['Attributes to be translated']
    if (!productUnit || !attribute) continue

    // Progressive fallback order
    const possibleKeys = [currentLanguage, `[${language}]`, FALLBACK_LANGUAGE]

    const translatedText = possibleKeys.map((key) => item[key]).find((value) => !!value?.trim()) || ''

    if (!map.has(productUnit)) {
      map.set(productUnit, new Map())
    }
    map.get(productUnit)?.set(attribute, translatedText)
  }

  return map
}

const getUniqueCategories = (data: ProductUnitData[]): string[] => {
  return [...new Set(data.map((item) => item.Category).filter(Boolean))]
}

const getMachineOptions = (data: ProductUnitData[]): string[] => {
  if (data.length === 0) return []

  const firstObject = data[0]
  return Object.keys(firstObject).filter((key) => !FIXED_FIELDS.has(key) && !key.startsWith(FEATURE_PREFIX))
}

const ProductUnits: FC<ProductUnitsProperties> = ({ jsonData }) => {
  const [selectedMachine, setSelectedMachine] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  const config = useMemo<Config>(() => {
    try {
      return JSON.parse(jsonData)
    } catch (error) {
      console.error('Failed to parse jsonData:', error)
      return {} as Config
    }
  }, [jsonData])

  const [productUnitsState] = useAutoGET<{ data: ProductUnitData[] }>(config.endpointUrl, {})
  const [translationsState] = useAutoGET<{ data: TranslationItem[] }>(config.translationsEndpointUrl, {})

  const currentLanguage = useMemo(() => {
    return extractLanguageFromUrl(window.location.pathname)
  }, [])

  const productUnitsData = useMemo<ProductUnitData[]>(() => {
    return productUnitsState.status === 'SUCCESS' ? productUnitsState.data.data : []
  }, [productUnitsState])

  const translationsData = useMemo<TranslationItem[]>(() => {
    return translationsState.status === 'SUCCESS' && Array.isArray(translationsState.data.data)
      ? translationsState.data.data
      : []
  }, [translationsState])

  const translationMap = useMemo(() => {
    return createTranslationMap(translationsData, currentLanguage)
  }, [translationsData, currentLanguage])

  const translatedProductUnitsData = useMemo<ProductUnitData[]>(() => {
    return productUnitsData.map((item) => {
      const productTranslations = translationMap.get(item.Title)
      if (!productTranslations) return item

      const translatedItem = { ...item }

      const translatedTitle = productTranslations.get('Title')
      if (translatedTitle) {
        translatedItem.Title = translatedTitle
      }

      const translatedCategory = productTranslations.get('Category')
      if (translatedCategory) {
        translatedItem.Category = translatedCategory
      }

      for (const key of Object.keys(translatedItem)) {
        if (key.startsWith(FEATURE_PREFIX)) {
          const translatedFeature = productTranslations.get(key)
          if (translatedFeature) {
            translatedItem[key] = translatedFeature
          }
        }
      }

      return translatedItem
    })
  }, [productUnitsData, translationMap])

  const categories = useMemo(() => {
    return getUniqueCategories(translatedProductUnitsData)
  }, [translatedProductUnitsData])

  const machines = useMemo(() => {
    return getMachineOptions(translatedProductUnitsData)
  }, [translatedProductUnitsData])

  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search)
    const machineParameter = parameters.get('machine')
    const categoryParameter = parameters.get('category')

    if (machineParameter) setSelectedMachine(machineParameter)
    if (categoryParameter) setSelectedCategory(categoryParameter)
  }, [])

  useEffect(() => {
    if (translatedProductUnitsData.length === 0) return

    if (selectedMachine && !machines.includes(selectedMachine)) {
      setSelectedMachine('')
    }
  }, [translatedProductUnitsData, machines, categories, selectedMachine, selectedCategory])

  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search)

    if (selectedMachine) {
      parameters.set('machine', selectedMachine)
    } else {
      parameters.delete('machine')
    }

    if (selectedCategory) {
      parameters.set('category', selectedCategory)
    } else {
      parameters.delete('category')
    }

    const queryString = parameters.toString()
    const newUrl = `${window.location.pathname}${queryString ? '?' + queryString : ''}`
    window.history.replaceState({}, '', newUrl)
  }, [selectedMachine, selectedCategory])

  const handleMachineSelect = useCallback((machine: string) => {
    setSelectedMachine(machine)
  }, [])

  const handleCategorySelect = useCallback(
    (category: string) => {
      setSelectedCategory(category)
    },
    [selectedCategory],
  )

  const filteredProductUnitsData = useMemo(() => {
    return translatedProductUnitsData.filter((item) => {
      const passesMachineFilter = !selectedMachine || (selectedMachine in item && item[selectedMachine] === '+')
      const passesCategoryFilter = !selectedCategory || item.Category.toLowerCase() === selectedCategory.toLowerCase()
      return passesMachineFilter && passesCategoryFilter
    })
  }, [translatedProductUnitsData, selectedMachine, selectedCategory])

  return (
    <ErrorWrapper>
      <div className="cmp-product-units__container">
        <div className="cmp-product-units__filters">
          <ProductUnitMachines
            selectMachineLabel={config.selectMachineLabel}
            machines={machines}
            selectedMachine={selectedMachine}
            onMachineSelect={handleMachineSelect}
            applyButtonLabel={config.applyButtonLabel}
            resetButtonLabel={config.resetButtonLabel}
            clearAriaLabel={config.closeButtonAriaLabel}
          />
          <ProductUnitCategoryTag
            categories={categories}
            selectedCategory={selectedCategory}
            onCategorySelect={handleCategorySelect}
          />
        </div>
        <div className="cmp-product-units">
          {filteredProductUnitsData.length > 0 ? (
            filteredProductUnitsData.map((item, index) => {
              const { _key, ...itemWithoutKey } = item
              return (
                <ProductUnit
                  key={item.Title || index}
                  backButtonLabel={config.backButtonLabel}
                  viewPageButtonLabel={config.viewPageButtonLabel}
                  closeButtonLabel={config.closeButtonAriaLabel}
                  productUnit={itemWithoutKey}
                />
              )
            })
          ) : (
            <NoResults title={config.noResultsLabel} description="" hasSpacing={true} />
          )}
        </div>
      </div>
    </ErrorWrapper>
  )
}

registerComponent('ProductUnits', ProductUnits)

export default ProductUnits
